<template>
  <div>
    <div v-if="!consentGiven" :class="$style.consent">
      <h2>{{ storeTranslations.translations?.['consentHeading'] || fallbackConsentHeading }}</h2>
      <div v-html="vendorConsentText" />
      <Button1XP
        :class="$style.consentButton"
        size="s"
        :text="consentButtonLabel"
        @click="handleContentButton"
      />
    </div>
    <template v-if="consentGiven">
      <form :class="$style.container">
        <h1>{{ config.intro.title }}</h1>
        <p>{{ config.intro.text }}</p>
        <div class="columns">
          <div class="column">
            <FormInput
              v-model.trim="v$.formData.name.$model"
              :bold-label="true"
              :error="v$.formData.name.$error && v$.$dirty && isSubmitted"
              :error-message="config.labels.name.errorMessage"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.name.label"
              :original-value="
                previousData.name && previousData.name !== '' ? previousData.name : null
              "
            />
          </div>
          <div class="column">
            <FormInput
              v-model.trim="v$.formData.company.$model"
              :bold-label="true"
              :error="v$.formData.company.$error && v$.$dirty && isSubmitted"
              :error-message="config.labels.company.errorMessage"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.company.label"
              :original-value="
                previousData.company && previousData.company !== '' ? previousData.company : null
              "
            />
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <FormInput
              v-model="formData.customer"
              :error="false"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.customer.label"
              :optional-label="config.labels.customer.optionLabel"
              :original-value="
                previousData.customer && previousData.customer !== '' ? previousData.customer : null
              "
            />
          </div>
          <div class="column">
            <FormInput
              v-model="formData.street"
              :error="false"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.street.label"
              :optional-label="config.labels.street.optionLabel"
              :original-value="
                previousData.street && previousData.street !== '' ? previousData.street : null
              "
            />
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <FormInput
              v-model.trim="v$.formData.zipCity.$model"
              :bold-label="true"
              :error="v$.formData.zipCity.$error && v$.$dirty && isSubmitted"
              :error-message="config.labels.zipCity.errorMessage"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.zipCity.label"
              :original-value="
                previousData.zipCity && previousData.zipCity !== '' ? previousData.zipCity : null
              "
            />
          </div>
          <div class="column">
            <FormSelect
              v-model.trim="v$.formData.country.$model"
              colour="white"
              :error="v$.formData.country.$error && v$.$dirty && isSubmitted"
              :error-message="config.labels.country.errorMessage"
              :initial-option="config.initialCountryOption"
              :label="config.labels.country.label"
              :options="config.countryOptions"
              :required="true"
              :value="config.initialCountryOption"
            />
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <FormInput
              v-model="formData.phone"
              :error="false"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.phone.label"
              :optional-label="config.labels.phone.optionLabel"
              :original-value="
                previousData.phone && previousData.phone !== '' ? previousData.phone : null
              "
            />
          </div>
          <div class="column">
            <FormInput
              v-model.trim="v$.formData.email.$model"
              :bold-label="true"
              :error="v$.formData.email.$error && v$.$dirty && isSubmitted"
              :error-message="config.labels.email.errorMessage"
              :form-submit-switch="formSubmitSwitch"
              :label="config.labels.email.label"
              :original-value="
                previousData.email && previousData.email !== '' ? previousData.email : null
              "
            />
          </div>
        </div>
        <div :class="$style.articleDataText">
          <b>{{ config.overview.title }}</b>
          <p>{{ config.overview.text }}</p>
        </div>
        <div v-if="formData.articleData && formData.articleData.length !== 0">
          <div
            v-for="(item, outerIndex) in formData.articleData"
            :key="outerIndex"
            :class="$style.articleData"
          >
            <b>{{ item.productTypes }}</b>
            <div v-for="(article, index) in item.articles" :key="index" class="columns is-mobile">
              <div class="column is-half">
                {{ config.labels.articleData.articleNumber }}: {{ article.number }}
                <tippy
                  ref="tippy"
                  :animate-fill="false"
                  animation="fade"
                  :arrow="true"
                  :class="$style.infoPopper"
                  :distance="7"
                  :duration="[200, 600]"
                  :interactive="true"
                  placement="bottom"
                  theme="1xp light tooltip"
                  :trigger="isMobile ? 'click' : 'mouseenter focus'"
                  :z-index="10000"
                  @init="initTippy"
                >
                  <Icon name="docs" size="medium" />

                  <template #content>
                    <div :class="$style.tippyContent" v-html="article.detailText" />
                  </template>
                </tippy>

                <a
                  :class="$style.trash"
                  :title="config.trashTitle"
                  @click.prevent.stop="removeItem(index, outerIndex)"
                >
                  <Icon name="close" size="medium" />
                </a>
              </div>
              <div class="column is-half">
                <FormInput
                  v-model="article.total"
                  :label="config.labels.articleData.articleTotal + ':'"
                  maxlength="4"
                  min="1"
                  type="number"
                />
              </div>
            </div>
          </div>
        </div>
        <p v-else :class="$style.warning">
          <span v-if="config.warning.title">{{ config.warning.title }}</span>
          {{ config.warning.text }}
        </p>
        <div class="columns">
          <div class="column">
            <FormTextarea
              v-model="formData.message"
              :columns="20"
              :error="false"
              :label="config.labels.message.label"
              :optional-label="config.labels.message.optionLabel"
              :rows="5"
            />
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-right-tablet">
            <Button
              colour="blue"
              :is-form="true"
              text="Senden"
              type="submit"
              @close="handleSubmit"
            />
          </div>
        </div>
        <div v-if="serverError" :class="$style.serverError">
          {{ config.serverErrorMessage }}
        </div>
        <ReCaptcha :execute-switch="executeSwitch" :submit="submit" />
      </form>
    </template>
  </div>
</template>

<script setup>
import { required, email } from '@vuelidate/validators';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';
import FormInput from '../FormInput/FormInput.vue';
import Icon from '../Icon/Icon.vue';
import FormSelect from '../FormSelect/FormSelect.vue';
import FormTextarea from '../FormTextarea/FormTextarea.vue';
import Button from '../Button/Button.vue';
import * as Storage from '../../shared/Storage';
import { isMobile } from '../../utils/breakpoints';
import StorageKeys from '../../globals/constants/storageKeys';
import submitRFQForm from '../../xhr/submitRFQForm';
import { useVuelidate } from '@vuelidate/core';
import { computed, onMounted, onUpdated, ref } from 'vue';
import { getMarketingConsent } from '../../globals/cookiebot';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import { useStoreTranslations } from '../../store/translations/translations';
import Button1XP from '../Button1XP/Button1XP.vue';

const noPlaceholderValue = (value) =>
  value.label ? value.label.indexOf('...') === -1 : value.indexOf('...') === -1;

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const storeTranslations = useStoreTranslations();
const dictionary = window.dictionary;

const vendor = cmpVendorIDs.GRECAPTCHA;
const consentGiven = ref(window.Cookiebot ? getMarketingConsent() : getVendorConsent(vendor.id));

const vendorConsentText = computed(
  () =>
    storeTranslations.translations?.['consentText']?.replaceAll('###VENDOR_NAME###', vendor.name) ||
    fallbackConsentText,
);
const consentButtonLabel = computed(
  () =>
    storeTranslations.translations?.['consentButtonLabel']?.replaceAll(/&apos;/g, "'") ||
    fallbackConsentButtonLabel,
);

const fallbackConsentHeading = computed(() => dictionary?.consentHeading);
const fallbackConsentText = dictionary?.consentText
  ?.replaceAll('###VENDOR_NAME###', vendor.name)
  .replaceAll('#<VENDOR_NAME>#', vendor.name);
const fallbackConsentButtonLabel = dictionary?.consentButtonLabel?.replaceAll(/&apos;/g, "'");

const previousData = ref({});
const isSubmitted = ref(false);
const executeSwitch = ref(false);
const formSubmitSwitch = ref(false);
const serverError = ref(false);

const formData = ref({
  name: '',
  company: '',
  customer: '',
  street: '',
  zipCity: '',
  country: '',
  phone: '',
  email: '',
  articleData: null,
  message: '',
  domain: '',
  pageUrl: '',
  language: '',
  token: '',
});

const rules = {
  formData: {
    name: {
      required,
    },
    company: {
      required,
    },
    zipCity: {
      required,
    },
    country: {
      required,
      noPlaceholderValue,
    },
    email: {
      required,
      email,
    },
  },
};

const v$ = useVuelidate(rules, { formData });

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(vendor.id);
  checkConsent();
};

const handleContentButton = () => {
  if (window.cmpConsentType === 'consentmanager') {
    window.__cmp('setVendorConsent', [vendor.id, 1], () => {
      consentGiven.value = getVendorConsent(vendor.id);
    });
  } else {
    if (window.Cookiebot) {
      window.Cookiebot.onaccept = () => {
        consentGiven.value = getMarketingConsent();
      };
      window.Cookiebot.show();
    }
    checkConsent();
  }
};

const checkConsent = () => {
  if (consentGiven.value) {
    initTippy();
  }
};

const registerConsentListenersWithDelay = (checkVendorConsent) => {
  const intervalId = setInterval(() => {
    if (typeof window.__cmp === 'function') {
      registerConsentListeners(checkVendorConsent);
      clearInterval(intervalId);
    }
  }, 100);
  setTimeout(() => clearInterval(intervalId), 5000);
};

onMounted(() => {
  if (window.Cookiebot) {
    consentGiven.value = getMarketingConsent();
  } else {
    consentGiven.value = getVendorConsent(vendor.id);
  }

  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListenersWithDelay(checkVendorConsent);
    setTimeout(() => {
      checkVendorConsent();
    }, 1000);
  } else {
    checkConsent();
  }

  checkLocalStorage();
  if (props.config.initialCountryOption) {
    formData.value.country = props.config.initialCountryOption;
  }
});

onUpdated(() => {
  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListeners(checkVendorConsent);
    checkVendorConsent();
  } else {
    checkConsent();
  }

  if (window.Cookiebot) {
    consentGiven.value = getMarketingConsent();
  } else {
    consentGiven.value = getVendorConsent(vendor.id);
  }
});

const checkLocalStorage = () => {
  formData.value.articleData = Storage.getLocalStorage(StorageKeys.ARTICLE_DATA, true);
};

const initTippy = (tippy) => {
  if (tippy && tippy.popper) {
    tippy.popper.id = 'infoPopper';
  }
};

const removeItem = (index, outerIndex) => {
  if (formData.value.articleData[outerIndex].articles.length > 1) {
    formData.value.articleData[outerIndex].articles.splice(index, 1);
  } else {
    formData.value.articleData.splice(outerIndex, 1);
  }
  Storage.setLocalStorage(StorageKeys.ARTICLE_DATA, formData.value.articleData, true);
};

const handleSubmit = () => {
  serverError.value = false;
  v$.value.$touch();
  isSubmitted.value = true;
  formSubmitSwitch.value = !formSubmitSwitch.value;
  if (!v$.value.$invalid) {
    executeSwitch.value = !executeSwitch.value;
    // submit()
  } else {
    previousData.value = JSON.parse(JSON.stringify(formData.value));
  }
};

const submit = (token) => {
  let preparedFormData = {
    ...formData.value,
    ...props.config.mailData,
    formId: props.config.formId,
    'mwf-formElement-sequence':
      'name,company,customer,street,zipCity,country,phone,email,articleData,message',
    'mwf-formLabel-name': 'Name',
    'mwf-formLabel-company': 'Company',
    'mwf-formLabel-customer': 'Customer',
    'mwf-formLabel-street': 'Street',
    'mwf-formLabel-zipCity': 'ZIP code / City',
    'mwf-formLabel-country': 'Country',
    'mwf-formLabel-phone': 'Phone',
    'mwf-formLabel-email': 'E-Mail',
    'mwf-formLabel-articleData': 'Article data',
    'mwf-formLabel-message': 'Message',
    domain: window.location.origin,
    pageUrl: window.location.pathname,
    language: window.currentLanguage || window._CAAS_config.language,
  };

  try {
    if (preparedFormData.articleData) {
      preparedFormData.articleData = preparedFormData.articleData
        .map((x) =>
          x.articles.map((article) => `${article.number} - ${x.productTypes} - ${article.total}`),
        )
        .flatMap((x) => x)
        .join('\n');
    }

    if (!preparedFormData.attachments) {
      preparedFormData.attachments = [];
    }

    if (preparedFormData.country && preparedFormData.country.value) {
      preparedFormData.country = preparedFormData.country.value;
    }

    preparedFormData.token = token;

    submitRFQForm(props.config.serviceUrl, preparedFormData)
      .then((response) => {
        if (response.status === 200) {
          Storage.removeLocalStorage(StorageKeys.ARTICLE_DATA);
          window.open(props.config.thankyouUrl, '_self');
        } else {
          serverError.value = true;
        }
      })
      .catch((error) => {
        serverError.value = true;
        console.error(error);
      });
  } catch (error) {
    console.warn(error);

    preparedFormData.token = token;

    submitRFQForm(props.config.serviceUrl, preparedFormData)
      .then((response) => {
        if (response.status === 200) {
          Storage.removeLocalStorage(StorageKeys.ARTICLE_DATA);
          window.open(props.config.thankyouUrl, '_self');
        } else {
          serverError.value = true;
        }
      })
      .catch((error) => {
        serverError.value = true;
        console.error(error);
      });
  }
};
</script>

<style module lang="scss">
@forward './RFQForm.scss';
</style>
